import React, { useEffect, useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from 'components/ui/dialog';
import { EmptyState } from 'components/EmptyState';
import api from 'lib/api';
import { toast } from 'sonner';
import { Plus, Truck, Trash2 } from 'lucide-react';
import { TRANSPORT_LABELS } from 'lib/format';
import { useLanguage } from 'lib/i18n';

export default function VehiclesPage() {
  const { t } = useLanguage();
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    vehicle_type: 'car', brand: '', model: '', registration: '', capacity_kg: 100, insurance_number: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const load = () => api.get('/users/me/vehicles').then(r => setVehicles(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post('/users/me/vehicles', { ...form, capacity_kg: Number(form.capacity_kg) });
      toast.success(t('vehicles.added'));
      setOpen(false);
      load();
    } catch { toast.error(t('vehicles.error')); }
  };
  const del = async (id) => {
    if (!window.confirm(t('vehicles.deleteConfirm'))) return;
    await api.delete(`/users/me/vehicles/${id}`);
    load();
  };

  return (
    <DashboardShell>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">{t('vehicles.title')}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-vehicle-button"><Plus size={16} className="mr-1"/> {t('vehicles.add')}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('vehicles.newTitle')}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>{t('vehicles.type')}</Label>
                <Select value={form.vehicle_type} onValueChange={v => set('vehicle_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="car">{TRANSPORT_LABELS.car}</SelectItem>
                    <SelectItem value="van">{TRANSPORT_LABELS.van}</SelectItem>
                    <SelectItem value="truck">{TRANSPORT_LABELS.truck}</SelectItem>
                    <SelectItem value="motorcycle">{TRANSPORT_LABELS.motorcycle}</SelectItem>
                    <SelectItem value="bicycle">{TRANSPORT_LABELS.bicycle}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t('vehicles.brand')}</Label><Input value={form.brand} onChange={e => set('brand', e.target.value)} data-testid="vehicle-brand-input" /></div>
              <div><Label>{t('vehicles.model')}</Label><Input value={form.model} onChange={e => set('model', e.target.value)} /></div>
              <div><Label>{t('vehicles.registration')}</Label><Input value={form.registration} onChange={e => set('registration', e.target.value)} /></div>
              <div><Label>{t('vehicles.capacity')}</Label><Input type="number" value={form.capacity_kg} onChange={e => set('capacity_kg', e.target.value)} /></div>
              <div className="sm:col-span-2"><Label>{t('vehicles.insuranceNumber')}</Label><Input value={form.insurance_number} onChange={e => set('insurance_number', e.target.value)} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>{t('vehicles.cancel')}</Button>
              <Button onClick={save} data-testid="vehicle-save-button">{t('vehicles.add')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {vehicles.length === 0 ? (
        <EmptyState icon={Truck} title={t('vehicles.none')} description={t('vehicles.emptyDescription')} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {vehicles.map(v => (
            <Card key={v.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center"><Truck size={18} /></div>
                  <div>
                    <div className="font-semibold">{v.brand} {v.model}</div>
                    <div className="text-xs text-muted-foreground">{TRANSPORT_LABELS[v.vehicle_type] || v.vehicle_type} • {v.registration}</div>
                  </div>
                </div>
                <div className="text-sm">{t('vehicles.capacityValue')} <b>{v.capacity_kg} kg</b></div>
                {v.insurance_number && <div className="text-xs text-muted-foreground">{t('vehicles.insuranceValue')} {v.insurance_number}</div>}
                <Button variant="outline" size="sm" className="mt-3" onClick={() => del(v.id)}><Trash2 size={14} className="mr-1"/> {t('vehicles.delete')}</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
